import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// Fetch consultations
export const fetchConsultations = createAsyncThunk(
  "consultation/fetchConsultations",
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch("/api/v1/admin/consultation_services");
      if (!res.ok) throw new Error("Failed to fetch consultation services");
      return await res.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Create consultation
export const createConsultation = createAsyncThunk(
  "consultation/createConsultation",
  async (data, { rejectWithValue }) => {
    try {
      const res = await fetch("/api/v1/admin/consultation_services", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ consultation_service: data }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.errors?.join(", ") || "Failed to create consultation");
      return result;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Update consultation
export const updateConsultationAsync = createAsyncThunk(
  "consultation/updateConsultation",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await fetch(`/api/v1/admin/consultation_services/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ consultation_service: data }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.errors?.join(", ") || "Failed to update consultation");
      return result;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Delete consultation
export const deleteConsultationAsync = createAsyncThunk(
  "consultation/deleteConsultation",
  async (id, { rejectWithValue }) => {
    try {
      const res = await fetch(`/api/v1/admin/consultation_services/${id}`, { method: "DELETE" });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.errors?.join(", ") || "Failed to delete consultation");
      }
      return id;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const consultationSlice = createSlice({
  name: "consultation",
  initialState: {
    consultations: [],
    loading: false,
    submitting: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // FETCH
      .addCase(fetchConsultations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConsultations.fulfilled, (state, action) => {
        state.consultations = action.payload;
        state.loading = false;
      })
      .addCase(fetchConsultations.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })

      // CREATE
      .addCase(createConsultation.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(createConsultation.fulfilled, (state, action) => {
        state.submitting = false;
        state.consultations.push(action.payload);
      })
      .addCase(createConsultation.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateConsultationAsync.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(updateConsultationAsync.fulfilled, (state, action) => {
        state.submitting = false;
        const index = state.consultations.findIndex(c => c.id === action.payload.id);
        if (index >= 0) state.consultations[index] = action.payload;
      })
      .addCase(updateConsultationAsync.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteConsultationAsync.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(deleteConsultationAsync.fulfilled, (state, action) => {
        state.submitting = false;
        state.consultations = state.consultations.filter(c => c.id !== action.payload);
      })
      .addCase(deleteConsultationAsync.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      });
  },
});

export default consultationSlice.reducer;
